import { formatAgeSec } from '@ijoin/components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

type Props = {
  backlog: {
    delayed: number[];
    inFlight: number[];
    messages: number[];
    oldestMessageAgeSec: number[];
    timestamps: Date[];
  };
  isLoading: boolean;
};

export const QueueBacklogSection = ({ backlog, isLoading }: Props) => (
  <Card sx={{ p: 1 }}>
    <CardContent>
      <Stack alignItems='center' direction='row' sx={{ mb: 2 }}>
        <Typography variant='subtitle1'>Total Backlog</Typography>
        <Tooltip
          arrow
          title='SQS-side view: visible + in-flight + delayed messages summed across all integrations, sampled every 30s.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {isLoading ? (
        <Alert severity='info'>Loading samples...</Alert>
      ) : backlog.timestamps.length === 0 ? (
        <Alert severity='info'>
          No queue samples in the selected time range. Samples are recorded
          every 30 seconds; data will appear shortly after deploy.
        </Alert>
      ) : (
        <Stack spacing={3}>
          <LineChart
            data-testid='totalBacklogChart'
            height={320}
            series={[
              {
                area: true,
                data: backlog.messages,
                label: 'Visible',
                showMark: false,
                stack: 'total'
              },
              {
                area: true,
                data: backlog.inFlight,
                label: 'In Flight',
                showMark: false,
                stack: 'total'
              },
              {
                area: true,
                data: backlog.delayed,
                label: 'Delayed',
                showMark: false,
                stack: 'total'
              }
            ]}
            xAxis={[{ data: backlog.timestamps, scaleType: 'time' }]}
          />

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Oldest Message Age (SLA exposure)
              </Typography>
              <Tooltip
                arrow
                title='How long the oldest unprocessed message has been waiting. Climbing trends mean consumers are falling behind.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <LineChart
              data-testid='oldestAgeChart'
              height={240}
              series={[
                {
                  data: backlog.oldestMessageAgeSec,
                  label: 'Oldest message',
                  showMark: false,
                  valueFormatter: (v: null | number) =>
                    v === null ? '' : formatAgeSec(v)
                }
              ]}
              xAxis={[{ data: backlog.timestamps, scaleType: 'time' }]}
            />
          </div>
        </Stack>
      )}
    </CardContent>
  </Card>
);
